import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Trash2, CalendarX2, TicketPercent, Power } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

// Sperrtage + Rabattcodes — Verwaltung direkt über dem Buchungen-Tab
export default function AdminBookingControls() {
  const [blocked, setBlocked] = useState([]);
  const [codes, setCodes] = useState([]);
  const [day, setDay] = useState("");
  const [note, setNote] = useState("");
  const [code, setCode] = useState("");
  const [percent, setPercent] = useState("");
  const [codeNote, setCodeNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, c] = await Promise.all([api.get("/blocked-days"), api.get("/discount-codes")]);
      setBlocked(d.data);
      setCodes(c.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const addDay = async () => {
    if (!day) return;
    setSaving(true);
    try {
      await api.post("/blocked-days", { date: day, note });
      toast.success(`${day.split("-").reverse().join(".")} gesperrt`);
      setDay("");
      setNote("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const removeDay = async (d) => {
    await api.delete(`/blocked-days/${d}`);
    toast.success("Sperrung aufgehoben");
    load();
  };

  const addCode = async () => {
    if (code.trim().length < 3 || !percent) return;
    setSaving(true);
    try {
      await api.post("/discount-codes", { code: code.trim(), percent: Number(percent), note: codeNote });
      toast.success(`Code ${code.trim().toUpperCase()} mit ${percent} % erstellt`);
      setCode("");
      setPercent("");
      setCodeNote("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleCode = async (id) => {
    await api.patch(`/discount-codes/${id}`);
    load();
  };

  const removeCode = async (id) => {
    if (!window.confirm("Code wirklich löschen?")) return;
    await api.delete(`/discount-codes/${id}`);
    toast.success("Code gelöscht");
    load();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4 mb-8">
      <div className="border border-white/10 bg-white/[0.02] p-5" data-testid="blocked-days-panel">
        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4 flex items-center gap-2">
          <CalendarX2 size={14} className="text-goldlight" /> Sperrtage (Tierbetreuung-Kalender)
        </p>
        <div className="flex flex-wrap gap-2 mb-4">
          <input
            type="date"
            value={day}
            onChange={(e) => setDay(e.target.value)}
            data-testid="blocked-day-input"
            className="bg-transparent border border-white/15 px-3 py-2 text-sm text-bone [color-scheme:dark]"
          />
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Grund (optional, z. B. Urlaub)"
            data-testid="blocked-day-note"
            className="flex-1 min-w-32 bg-transparent border border-white/15 px-3 py-2 text-sm text-bone placeholder:text-zinc-600"
          />
          <button
            onClick={addDay}
            disabled={saving || !day}
            data-testid="blocked-day-add"
            className="inline-flex items-center gap-2 border border-goldlight/50 text-goldlight px-4 py-2 text-[11px] uppercase tracking-[0.2em] hover:bg-goldlight hover:text-coal transition-colors disabled:opacity-40"
          >
            <Plus size={13} /> Sperren
          </button>
        </div>
        {blocked.length === 0 ? (
          <p className="text-sm text-zinc-600">Keine Sperrtage — der Kalender folgt nur den normalen Zeiten (Fr ab 17, Sa/So ganztags).</p>
        ) : (
          <ul className="space-y-2">
            {blocked.map((b) => (
              <li key={b.date} className="flex items-center justify-between text-sm border-b border-white/5 pb-2" data-testid={`blocked-day-${b.date}`}>
                <span className="text-zinc-300">{b.date.split("-").reverse().join(".")}{b.note ? ` — ${b.note}` : ""}</span>
                <button onClick={() => removeDay(b.date)} data-testid={`blocked-day-remove-${b.date}`} className="text-zinc-600 hover:text-red-400 transition-colors">
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border border-white/10 bg-white/[0.02] p-5" data-testid="discount-codes-panel">
        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-4 flex items-center gap-2">
          <TicketPercent size={14} className="text-goldlight" /> Rabattcodes
        </p>
        <div className="flex flex-wrap gap-2 mb-4">
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="CODE"
            data-testid="discount-code-input"
            className="w-28 bg-transparent border border-white/15 px-3 py-2 text-sm text-bone placeholder:text-zinc-600 uppercase"
          />
          <input
            value={percent}
            onChange={(e) => setPercent(e.target.value.replace(/\D/g, "").slice(0, 2))}
            placeholder="%"
            inputMode="numeric"
            data-testid="discount-percent-input"
            className="w-16 bg-transparent border border-white/15 px-3 py-2 text-sm text-bone placeholder:text-zinc-600"
          />
          <input
            value={codeNote}
            onChange={(e) => setCodeNote(e.target.value)}
            placeholder="Notiz (optional)"
            data-testid="discount-note-input"
            className="flex-1 min-w-28 bg-transparent border border-white/15 px-3 py-2 text-sm text-bone placeholder:text-zinc-600"
          />
          <button
            onClick={addCode}
            disabled={saving || code.trim().length < 3 || !percent || Number(percent) < 1 || Number(percent) > 99}
            data-testid="discount-code-add"
            className="inline-flex items-center gap-2 border border-goldlight/50 text-goldlight px-4 py-2 text-[11px] uppercase tracking-[0.2em] hover:bg-goldlight hover:text-coal transition-colors disabled:opacity-40"
          >
            <Plus size={13} /> Erstellen
          </button>
        </div>
        {codes.length === 0 ? (
          <p className="text-sm text-zinc-600">Noch keine Codes — lege oben deinen ersten an (z. B. FREUNDE20 mit 20 %).</p>
        ) : (
          <ul className="space-y-2">
            {codes.map((c) => (
              <li key={c.id} className="flex items-center justify-between gap-3 text-sm border-b border-white/5 pb-2" data-testid={`discount-row-${c.code}`}>
                <span className={c.active ? "text-zinc-200" : "text-zinc-600 line-through"}>
                  <b className="font-mono">{c.code}</b> — {c.percent} %{c.note ? ` · ${c.note}` : ""}
                  <span className="text-zinc-600"> · {c.used_count}× eingelöst</span>
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  <button onClick={() => toggleCode(c.id)} data-testid={`discount-toggle-${c.code}`} title={c.active ? "Deaktivieren" : "Aktivieren"} className={`transition-colors ${c.active ? "text-goldlight" : "text-zinc-600"} hover:text-bone`}>
                    <Power size={14} />
                  </button>
                  <button onClick={() => removeCode(c.id)} data-testid={`discount-delete-${c.code}`} className="text-zinc-600 hover:text-red-400 transition-colors">
                    <Trash2 size={14} />
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
